import React, { useState } from "react";
import { Button, Modal, Tooltip, Tag } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";

interface SyncResult {
  lang: string;
  quiz_id?: string;
  status: "created" | "updated" | "failed";
  error?: string;
}

interface SyncChildrenButtonProps {
  quizId: string;
  parentId: string | undefined | null;
  apiUrl: string;
}

const SyncChildrenButton: React.FC<SyncChildrenButtonProps> = ({ quizId, parentId, apiUrl }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const isDisabled = !!parentId;

  const handleSync = async () => {
    setLoading(true);
    const token = localStorage.getItem("api-token");

    try {
      const response = await axios.post(
        `${apiUrl}/quiz/${quizId}/update-children`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const results: SyncResult[] = response.data.results || [];

      Modal.info({
        title: t("quiz.sync_children.success_title"),
        width: 600,
        content: (
          <div style={{ marginTop: 16 }}>
            {results.map((r) => (
              <div key={r.lang} style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <strong>{r.lang.toUpperCase()}</strong>
                {r.status === "failed" ? (
                  <Tag color="red">{t("quiz.sync_children.status_failed")}</Tag>
                ) : (
                  <Tag color="green">
                    {r.status === "created"
                      ? t("quiz.sync_children.status_created")
                      : t("quiz.sync_children.status_updated")}
                  </Tag>
                )}
                {r.quiz_id && <span style={{ color: "#888", fontSize: 12 }}>{r.quiz_id}</span>}
                {r.error && <span style={{ color: "red", fontSize: 12 }}>{r.error}</span>}
              </div>
            ))}
          </div>
        ),
      });
    } catch (error) {
      const errorMessage =
        (axios.isAxiosError(error) && error.response?.data?.message) ||
        "Something went wrong. Please try again.";

      Modal.error({
        title: t("quiz.sync_children.error_title"),
        content: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={isDisabled ? t("quiz.sync_children.tooltip_disabled") : undefined}>
      <Button
        icon={<SyncOutlined spin={loading} />}
        loading={loading}
        disabled={isDisabled || loading}
        onClick={handleSync}
      >
        {t("quiz.sync_children.button")}
      </Button>
    </Tooltip>
  );
};

export default SyncChildrenButton;
